import { type HttpContext } from '@angular/common/http';
import { requestContextForTenantItems } from '@modules/common/services/base-stitch.service';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import type { AccountAppSubjectsType } from '@sharedlib/common/permissions/subjects/AccountAppSubjects';
import type { Actions } from '@sharedlib/common/permissions/types/Permissions';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { ItemChangesComponent } from '@modules/team-item-changes/item-changes.component';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import {
  ITEM_REVIEW_INLINE_HOST,
  type ItemReviewTarget,
} from '@modules/common/components/item-review/item-review-dialog.service';
import { input, viewChild } from '@angular/core';
import { ItemHandoverComponent } from '@modules/common/components/item-handover/item-handover.component';
import {
  ITEM_HANDOVER_INLINE_HOST,
  type ItemHandoverTarget,
} from '@modules/common/components/item-handover/item-handover-dialog.service';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { ItemQuickCommentComponent } from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { messageFooterLocation } from '@modules/common/components/item-workspace/item-workspace-location';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { TitleByMessagesFilterPipe } from '@modules/messages/pipes/title-by-messages-filter.pipe';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { itemActivityTenant } from '@modules/common/components/item-workspace/item-activity-tenant';
import {
  Component,
  computed,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { SearchLocationBadgeComponent, type SearchLocationBadgeType } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { Application } from '@modules/common/types/application';
import { MessageFolderSummariesCache } from '@modules/messages/services/message-folder-summaries.cache';
import {
  getMessageVirtualLocationBadge,
  type MessageLocationBadge,
} from '@modules/messages/utils/message-location-badge';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AttachmentsService } from '@modules/attachments/services/attachments.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { StitchItemEventsService } from '@modules/common/services/stitch-item-events.service';
import { LinkedInfo } from '@modules/linked-info/types/linked-info';
import { Tab } from '@modules/common/types/tab';
import { UILocations } from '@modules/common/types/uiLocations';
import { Knot } from '@modules/knots/types/knot';
import { Message, MessageFormGroupConfig } from '@modules/messages/types/message';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { MessagesService } from '../../services/messages.service';

@Component({
  selector: 'app-message2-form',
  templateUrl: './message-form.component.html',
  styleUrls: ['./message-form.component.less'],
  standalone: false,
  providers: [
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(MessageFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    WorkspaceIdentityService,
    {
      provide: ITEM_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(MessageFormComponent);
        return { open: (target: ItemHandoverTarget): boolean => form.openInlineAssignment(target) };
      },
    },
  ],
})
export class MessageFormComponent
  extends FullFormBaseComponent<StitchType.MESSAGE, Message, MessageFormGroupConfig>
  implements OnInit, OnChanges, OnDestroy
{
  public readonly insideModal = input(false);
  public readonly reviewApi = inject(MessagesService).reviewApi;
  private readonly reviewControl = viewChild(ItemReviewComponent);
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));
  public openInlineReview(target: ItemReviewTarget): boolean {
    const control = this.reviewControl();
    if (
      target.stitchType !== StitchType.MESSAGE ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openRequest();
    return true;
  }

  public readonly handoverApi = inject(MessagesService).handoverApi;
  protected readonly StitchType = StitchType;
  private readonly handoverControl = viewChild(ItemHandoverComponent);
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  private readonly accountKind = inject(AccountKindService);
  private readonly handoverPermissions: PermissionsService<Actions, AccountAppSubjectsType> =
    inject(PermissionsService);
  protected get handoverTenantId(): string | undefined {
    return this.accountKind.isTeam() && itemWorkspace(this.workspaceIdentity(), this.message) === 'team'
      ? this.message.accountId
      : undefined;
  }
  protected get handoverItemId(): string | undefined {
    return this.message.id;
  }
  protected get handoverCanAssign(): boolean {
    return canEditDocumentsIn(this.handoverPermissions, this.handoverTenantId);
  }
  public openInlineAssignment(target: ItemHandoverTarget): boolean {
    const control = this.handoverControl();
    if (
      !this.handoverCanAssign ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openAssign();
    return true;
  }
  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);

  protected readonly workspaceComponents = {
    'app-item-changes': ItemChangesComponent,
    'app-item-team-label': ItemTeamLabelComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-quick-comment': ItemQuickCommentComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
  };

  protected readonly workspaceIdentity = toSignal(
    inject(WorkspaceIdentityService)
      .current()
      .pipe(
        take(1),
        catchError(() => of(undefined)),
      ),
    { initialValue: undefined },
  );
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    inject(WorkspaceIdentityService),
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  private readonly searchApplied = toSignal(inject(AdvancedSearchService).isAdvancedSearchApplied(Application.mail), {
    initialValue: false,
  });
  /**
   * One gate for both branches: the shared factory takes it rather than building its own from the same
   * search, so there is a single source deciding whether any badge shows at all.
   */
  private readonly badgeShown = computed(() => this.searchApplied() || this.openedFromHub());
  /**
   * A manual folder resolves through the shared factory; the virtual statuses
   * (inbox/other-mail/sent/draft/scheduled) are classified from the message alone, exactly as the list row
   * does it, so they need no load.
   */
  private readonly folderBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.folderId,
    cache: inject(MessageFolderSummariesCache),
    badgeType: 'mail-folder',
    shown: this.badgeShown,
  });
  private readonly savedMessage = toSignal(
    this.stitchItemChanged.pipe(
      skip(1),
      map(() => this.stitchItem),
    ),
    { initialValue: null },
  );
  protected readonly locationBadge = computed<MessageLocationBadge | null>(() => {
    if (!this.badgeShown()) {
      return null;
    }

    const message = this.savedMessage();
    if (!message) {
      return null;
    }

    return message.folderId ? this.folderBadge() : getMessageVirtualLocationBadge(message);
  });

  /**
   * The header's badge for an email outside a manual folder, in every mail view (Inbox, VIP, Flagged, Follow Up,
   * Snoozed, Drafts, Archive…), not only in search (Johanna, 2026-10-21): its system place, the search rows'
   * classification, with Archive for archived mail.
   */
  protected readonly headerLocation = computed<MessageLocationBadge | null>(() => {
    const message = this.savedMessage();
    if (!message || message.folderId) {
      return null;
    }
    return getMessageVirtualLocationBadge(message) ?? (message.archived && !message.deleted ? { type: 'archive' } : null);
  });

  /**
   * The header's badge under the icons: the email's folder as the blue folder badge with its icon, else its place
   * (Inbox, Other Mail, Sent, Draft, Scheduled, Archive), drawn as search and the Knowledge Hub draw it, so the
   * dark grey Inbox reads the same everywhere (Johanna, 2026-10-21; it was the prototype's lighter MailboxBadge).
   */
  protected readonly headerBadge = computed<{ type: SearchLocationBadgeType; name: string; label: string } | null>(() => {
    const folder = this.headerFolder();
    if (folder) {
      return { type: 'mail-folder', name: folder.title ?? '', label: folder.title ?? '' };
    }
    const badge = this.headerLocation() ?? this.locationBadge();
    if (!badge) {
      return null;
    }
    const label = SearchLocationBadgeComponent.CONFIG[badge.type].label ?? badge.name ?? '';
    return label ? { type: badge.type, name: badge.name ?? '', label } : null;
  });

  /** A draft's subject line starts with "[Draft]" (display only; the stored subject is untouched). */
  protected readonly isDraft = computed(() => {
    const message = this.savedMessage();
    return !!message && getMessageVirtualLocationBadge(message)?.type === 'draft';
  });

  /**
   * Mail attachments are message parts (loaded by `app-attachments`), not stitched
   * FILE items, so the Attachments tab badge starts from the message's own count and
   * is re-derived from the live attachment list when a file is trashed/restored
   * (the stored `attachmentsCount` column is not updated by trash) — see ngOnInit.
   */
  private readonly mailAttachmentsCount = signal<number>(0);
  protected override readonly attachmentsTabCount = computed(() => this.mailAttachmentsCount());

  /**
   * File ids of this message's attachment parts — scopes FILE trash/restore signals
   * to this message. `undefined` means the ids are not known (initial lookup pending
   * or failed); in that state, any FILE trash/restore retries the lookup rather than
   * being ignored.
   */
  private attachmentFileIds?: Set<string>;

  protected readonly footerBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.folderId,
    cache: inject(MessageFolderSummariesCache),
    badgeType: 'mail-folder',
    shown: computed(() => !!this.displayWorkspaceIdentity()),
  });

  private readonly titleByMessagesFilter = new TitleByMessagesFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(MailAppStateService, {
    appLabel: APP_CRUMBS.mail,
    viewTitle: (folder) => this.titleByMessagesFilter.transform(folder, null, true),
  });

  /** [workspace ›] Mail app › the list's view › this email. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      // Where the email is (Inbox, Spam, Sent…, or its folder), never the view the side nav shows now (Johanna,
      // 2026-10-21: an Inbox email read "Spam" after a click on Spam).
      view: this.headerBadge()?.label,
      container: this.headerFolder()?.title,
      fallbackView: 'Inbox',
      itemTitle: this.message.title || 'No subject',
      itemId: this.message?.id,
    });
  }

  private readonly headerFolderId = computed(() => {
    this.workspaceRevision();
    return this.stitchItem?.folderId ?? null;
  });

  private readonly folderSummaries = inject(MessageFolderSummariesCache);
  private readonly mailboxScope = inject(MailboxTenantScopeService, { optional: true });

  /**
   * The mailbox to read this email's folder in when it is not the one on screen: the rail's folder trees say so with
   * the form's `requestContext`; an email opened from the search popup's other scope says so with its own `accountId`
   * (Codex r4 P2 on #2758, reproduced live: its folder badge read the screen's mailbox and named nothing). The mailbox
   * on screen is the selected one, or — in a personal context, where that is `null` — the account itself.
   */
  private readonly otherMailboxContext = computed((): HttpContext | null => {
    this.workspaceRevision();
    if (this.requestContext) {
      return this.requestContext;
    }
    const owner = this.stitchItem?.accountId;
    const onScreen = this.mailboxScope?.effectiveTenantId() ?? this.spaceContextStore.contextId();
    return owner && onScreen && owner !== onScreen ? (this.itemRequestContext() ?? null) : null;
  });

  /** The folder from the shared cache, whose reads carry the mailbox on screen. */
  private readonly screenMailboxFolder = this.folderSummaries.get(
    computed(() => (this.otherMailboxContext() ? null : this.headerFolderId())),
  );

  /**
   * The folder of an email from another mailbox (above). The cache's reads would carry the mailbox on screen and find
   * no such folder, so it is read here under the email's own mailbox, as its attachments are (messages/AGENTS.md).
   */
  private readonly otherMailboxFolder = toSignal(
    toObservable(computed(() => ({ folderId: this.headerFolderId(), context: this.otherMailboxContext() }))).pipe(
      switchMap(({ folderId, context }) => {
        if (!folderId || !context) {
          return of(undefined);
        }
        return this.folderSummaries.readInMailbox(folderId, context).pipe(catchError(() => of(undefined)));
      }),
    ),
    { initialValue: undefined },
  );

  /** The folder this email is filed in, named by the header's container badge and the breadcrumbs. */
  protected readonly headerFolder = computed(() =>
    this.otherMailboxContext() ? this.otherMailboxFolder() : this.screenMailboxFolder(),
  );

  @Input() message = new Message();

  private messageChanged: Subject<Message> = new Subject<Message>();

  public changesKey = 'message';
  tabs: Tab[] = [
    { title: 'Email', value: 'message' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  public selectedKnots: Knot[] = [];
  protected readonly highlights = signal<string[]>([]);
  public messageImagesLoaded = false;

  protected readonly UILocations = UILocations;

  protected readonly isScheduled = signal(false);
  protected readonly scheduledSendTimeValue = signal<Date | null>(null);

  private readonly messagesService = inject(MessagesService);
  private readonly stitchItemEventsService = inject(StitchItemEventsService);
  private readonly attachmentsService = inject(AttachmentsService);

  constructor() {
    super(inject(Injector), inject(MessagesService));
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    // The pop-up closes once its message is trashed; the signal is only sent on success and names the message's
    // mailbox, which inside a team is not the space on screen (SBUX-1147).
    if (this.insideModal()) {
      this.stitchEventsService
        .getEvents(
          () => [this.spaceContextStore.contextId(), this.message?.accountId].filter((id): id is string => !!id),
          StitchType.MESSAGE,
          SignalStitchEventType.MOVED_TO_TRASH,
        )
        .pipe(
          filter((payload) => !!this.message?.id && payload.ids.includes(this.message.id)),
          takeUntil(this.alive),
        )
        .subscribe(() => this.handleClose());
    }

    // Attachments tab badge: the stored attachmentsCount is written at ingest/save and
    // never updated by trash, while the tab hides attachments of trashed files. So show
    // the stored count immediately, verify it against the live attachment list when the
    // message claims to have attachments, and re-derive it on FILE trash/restore signals
    // that touch one of this message's own attachment files (not space-wide events).
    const fileTrashChanges$ = this.stitchEventsService.getEvents(this.spaceContextStore.contextId(), StitchType.FILE, [
      SignalStitchEventType.MOVED_TO_TRASH,
      SignalStitchEventType.RESTORED_FROM_TRASH,
    ]);

    this.stitchItemChanged
      .pipe(
        tap(() => (this.attachmentFileIds = undefined)),
        switchMap(() => {
          const storedCount = this.message?.attachmentsCount ?? 0;

          // One serialized lane for the initial verification and the event-driven
          // refetches: the shared switchMap lets a newer trigger cancel an older
          // in-flight lookup, so a slow initial response can never overwrite a
          // fresher event-driven count.
          return merge(
            of(null),
            fileTrashChanges$.pipe(
              filter((payload) => {
                if (!this.message?.id) {
                  return false;
                }

                // Ids unknown (initial lookup pending/failed): retry on any FILE
                // trash/restore while the message claims attachments, instead of
                // staying blind until the form is rebound.
                const fileIds = this.attachmentFileIds;
                if (!fileIds) {
                  return (this.message?.attachmentsCount ?? 0) > 0;
                }

                return payload.ids?.some((id) => fileIds.has(id));
              }),
            ),
          ).pipe(
            switchMap((_, index) => {
              const isInitial = index === 0;

              if (isInitial && (!this.message?.id || storedCount === 0)) {
                return of(storedCount);
              }

              const fetched$ = this.fetchVisibleAttachmentsCount().pipe(catchError(() => EMPTY));

              // Show the stored count immediately while the initial verification runs.
              return isInitial ? fetched$.pipe(startWith(storedCount)) : fetched$;
            }),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe((count) => this.mailAttachmentsCount.set(count));

    this.kpService
      .getSelectedKnots()
      .pipe(
        startWith<Knot[]>([]),
        map((knots) => {
          this.selectedKnots = knots;

          return knots.map((knot) => knot.name);
        }),
        takeUntil(this.alive),
      )
      .subscribe((highlights) => this.highlights.set(highlights));

    // Listens for the item-specific events and updates specific properties
    this.messageChanged
      .pipe(
        // only trigger when the item with the new id has been passed
        distinctUntilChanged((prev, curr) => {
          return prev?.id === curr?.id;
        }),
        switchMap(() => {
          // Cancel subscription and return EMPTY observable if this.message was unset for some reason
          // if the item is set, we subscribe to Signal events
          return this.message
            ? merge(
                this.stitchItemEventsService.onRead(this.message),
                this.stitchItemEventsService.onUnRead(this.message),
                this.stitchItemEventsService.onFlagged(this.message),
                this.stitchItemEventsService.onUnFlagged(this.message),
                this.stitchItemEventsService.onPinned(this.message),
                this.stitchItemEventsService.onUnPinned(this.message),
              )
            : EMPTY;
        }),
        takeUntil(this.alive),
      )
      .subscribe();

    this.messageChanged.next(this.message);
    this.updateScheduledSignals();
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
    if (changes.tab && this.tab === 'access' && this.insideTeam) this.openWorkspaceAccess();

    if ('message' in changes) {
      // Message input changed, restart the stitch event subscription
      this.messageChanged.next(this.message);
      this.updateScheduledSignals();
      this.messageImagesLoaded = false;
      if (this.message?.unread) {
        // In the message's own mailbox, not the one on screen: an email opened from the search popup's other scope
        // was marked read in the wrong mailbox and stayed unread in its own (searchopen r2, Codex P2 on #2758).
        this.messagesService.read({ ids: [this.message.id] }, true, {
          toast: false,
          requestContext: this.itemRequestContext(),
        });
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  /**
   * Actions
   */

  forward(action: 'forward' | 'reply' | 'replyAll'): void {
    this.messagesService.composeMessage({
      reply: this.stitchItem,
      forward: [this.stitchItem],
      action,
      injector: this.injector,
      messageImagesAreLoaded: this.messageImagesLoaded,
    });
  }

  /**
   * Cancels the scheduled send for this message, converting it to a draft.
   */
  cancelSchedule(): void {
    if (!this.message?.id || !this.message.isScheduled()) {
      return;
    }

    this.messagesService
      .unschedule(this.message, { emit: true, toast: true, message: 'Moved to Drafts' })
      .pipe(takeUntil(this.alive))
      .subscribe(() => this.handleClose());
  }

  /**
   * Helpers
   */

  get stitchItem(): Message {
    return this.message;
  }

  private updateScheduledSignals(): void {
    this.isScheduled.set(this.message?.isScheduled() ?? false);
    this.scheduledSendTimeValue.set(this.message?.scheduledAt ? new Date(this.message.scheduledAt) : null);
  }

  // Treat a scheduling-state change (schedule-send / reschedule / unschedule) as
  // server-managed so the base handler silently re-applies it with no confirm modal,
  // even when the form is dirty (save-and-schedule is an explicit user action).
  /**
   * Counts what the Attachments tab actually renders: non-inline message parts whose
   * file exists and is not trashed (mirrors AttachmentsComponent's load pipeline).
   * Also remembers this message's attachment file ids so trash/restore signals can be
   * scoped to files that actually belong to it.
   */
  /**
   * The message is the tenant carrier for the reads this reader makes about it, as in `app-attachments` (#1051): its
   * `accountId` names its mailbox, which need not be the one on screen. An explicit `requestContext` still wins.
   */
  private itemRequestContext(): HttpContext | undefined {
    return this.message
      ? requestContextForTenantItems([this.message], { requestContext: this.requestContext })
      : undefined;
  }

  private fetchVisibleAttachmentsCount(): Observable<number> {
    return this.attachmentsService.listAll(this.message, { inline: false }, this.itemRequestContext()).pipe(
      switchMap((attachments) => {
        // Cache the ids as soon as the part list is known — before resolving files —
        // so a failure in the resolution step doesn't leave the event filter blind.
        this.attachmentFileIds = new Set(
          attachments.map((attachment) => attachment.fileId).filter((fileId): fileId is string => !!fileId),
        );

        const linkedInfo = attachments
          .filter((attachment) => attachment.fileId)
          .map((attachment) => new LinkedInfo({ id: attachment.fileId, itemType: StitchType.FILE }));

        return zip(this.linkedInfoService.fill(linkedInfo, this.message?.accountId), of(attachments));
      }),
      map(
        ([linkedInfoData, attachments]) =>
          this.attachmentsService
            .populateAttachments(attachments, linkedInfoData)
            .filter((attachment) => attachment.file && !attachment.file.deleted).length,
      ),
    );
  }

  protected override mergeServerManagedFields(updatedItem: Message): boolean {
    const scheduleChanged =
      updatedItem.state !== this.message?.state ||
      (updatedItem.scheduledAt?.getTime() ?? null) !== (this.message?.scheduledAt?.getTime() ?? null);

    if (!scheduleChanged) {
      return false;
    }

    this.message.state = updatedItem.state;
    this.message.scheduledAt = updatedItem.scheduledAt;
    this.updateScheduledSignals();
    return true;
  }

  protected shouldRefreshList(prev: object, current: object): boolean {
    return Message.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Message {
    return Message.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.message.asFormGroup({ maxAttachmentSize: this.maxUploadSize() });
  }

  public onLoadImages(loaded: boolean): void {
    this.messageImagesLoaded = loaded;
  }
  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.message);
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.message,
      noun: 'Inbox',
      collection: messageFooterLocation(this.message, this.footerBadge()?.name),
      archived: this.form?.controls.archived.value ?? false,
      deleted: this.form?.controls.deleted.value ?? false,
    };
  }
  protected get workspaceLabelInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.message };
  }
  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceAccessInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.message };
  }

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceAccessInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get itemChangesInputs(): Record<string, unknown> | undefined {
    const item = this.stitchItem;
    const tenantId = itemActivityTenant(this.spaceContextStore, item);
    return tenantId ? { path: '/api/mail/messages', itemId: item.id, tenantId } : undefined;
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts().flatMap((tab) =>
      tab.value === 'comments' && this.itemChangesInputs ? [tab, { title: 'Activity', value: 'activity' }] : [tab],
    );
    return this.insideTeam && this.selectedTab === 'access' ? [...tabs, { title: 'Access', value: 'access' }] : tabs;
  }
  protected readonly openItemComments = (): void => {
    this.selectedTab = 'comments';
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.message,
      noun: 'email',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
    };
  }
}
